import type { LoaderFunctionArgs, MetaFunction } from 'react-router'
import { Link, useLoaderData } from 'react-router'
import { db } from '@afficheservices/db'
import { sql } from 'drizzle-orm'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema } from '~/components/structured-data'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import { TASKS_BY_SERVICE, DEAD_TASK_SLUGS } from '~/lib/tarifs-tasks-data'
import { SERVICE_LABELS } from '~/lib/programmatic-seo'
import { formatCityName } from '~/lib/city-format'
import { Euro, ArrowLeft } from 'lucide-react'

/**
 * /tarifs/[service] — second level of the hub that used to 404.
 *
 * Lists the real, indexable /tarifs/[service]/[city]/[task] URLs grouped by
 * prestation. The list comes from seo_index_whitelist ∪ programmatic_seo_content
 * (the two tables that decide whether a leaf renders index,follow), so the hub
 * can never link to a page that answers noindex.
 */
export function headers() { return STATIC_PAGE_HEADERS }

// Enough links to be a useful directory, few enough to keep the HTML small and
// the crawl shallow. Deeper pages are reachable via rel=prev/next.
const PER_PAGE = 600

export async function loader({ params, request }: LoaderFunctionArgs) {
  const service = params.service ?? ''
  const tasks = (TASKS_BY_SERVICE[service] ?? []).filter((t) => !DEAD_TASK_SLUGS.has(t.slug))
  if (tasks.length === 0) throw new Response('Page non trouvée', { status: 404 })

  const rows = (await db.execute(sql`
    SELECT split_part(path, '/', 4) AS city, split_part(path, '/', 5) AS task
    FROM seo_index_whitelist
    WHERE path LIKE ${'/tarifs/' + service + '/%'}
    UNION
    SELECT city_slug AS city, task_slug AS task
    FROM programmatic_seo_content
    WHERE route_type = 'tarifs' AND slug = ${service} AND task_slug IS NOT NULL
    ORDER BY 2, 1
  `)) as unknown as Array<{ city: string; task: string }>

  const live = new Set(tasks.map((t) => t.slug))
  const all = rows.filter((r) => live.has(r.task))

  const page = Math.max(1, parseInt(new URL(request.url).searchParams.get('page') ?? '1', 10) || 1)
  const pageCount = Math.max(1, Math.ceil(all.length / PER_PAGE))
  if (page > pageCount) throw new Response('Page non trouvée', { status: 404 })

  const slice = all.slice((page - 1) * PER_PAGE, page * PER_PAGE)
  const byTask = tasks
    .map((t) => ({
      ...t,
      cities: slice.filter((r) => r.task === t.slug).map((r) => r.city),
    }))
    .filter((t) => t.cities.length > 0)

  return {
    service,
    name: SERVICE_LABELS[service]?.name ?? service,
    artisan: SERVICE_LABELS[service]?.artisan ?? service,
    byTask,
    total: all.length,
    page,
    pageCount,
  }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data) return [{ title: 'Page introuvable — AfficheServices' }]
  const base = `https://www.afficheservices.fr/tarifs/${data.service}`
  const url = data.page > 1 ? `${base}?page=${data.page}` : base
  const suffix = data.page > 1 ? ` — page ${data.page}` : ''
  const tags: ReturnType<MetaFunction> = [
    { title: `Tarifs ${data.name.toLowerCase()} ${new Date().getFullYear()} par ville et prestation${suffix} | AfficheServices` },
    { name: 'description', content: `Prix ${data.artisan} détaillés par prestation dans ${data.total} villes : fourchette de tarif, facteurs de variation et devis gratuits sous 48h.` },
    { tagName: 'link', rel: 'canonical', href: url },
  ]
  if (data.page > 1) tags.push({ tagName: 'link', rel: 'prev', href: data.page === 2 ? base : `${base}?page=${data.page - 1}` })
  if (data.page < data.pageCount) tags.push({ tagName: 'link', rel: 'next', href: `${base}?page=${data.page + 1}` })
  return tags
}

export default function TarifsServiceHub() {
  const { service, name, artisan, byTask, total, page, pageCount } = useLoaderData<typeof loader>()
  const base = `/tarifs/${service}`

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Tarifs', url: 'https://www.afficheservices.fr/tarifs' },
        { name: `Tarifs ${name}`, url: `https://www.afficheservices.fr${base}` },
      ]} />

      <section className="bg-gradient-to-br from-slate-900 to-emerald-900 px-4 py-12 text-white">
        <div className="mx-auto max-w-5xl">
          <Link to="/tarifs" className="inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> Tous les tarifs
          </Link>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-4xl">Tarifs {name.toLowerCase()} par ville</h1>
          <p className="mt-3 max-w-2xl text-slate-300">
            <Euro className="mr-1 inline h-4 w-4" />
            {total} pages de prix {artisan}, prestation par prestation, avec la fourchette constatée localement.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 lg:px-6">
        {byTask.map((t) => (
          <section key={t.slug} className="mb-10">
            <h2 className="text-lg font-bold text-slate-900">
              {t.label} <span className="ml-1 text-sm font-normal text-slate-500">{t.priceRange}</span>
            </h2>
            <p className="mt-1 text-sm text-slate-600">{t.desc}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {t.cities.map((city) => (
                <Link
                  key={city}
                  to={`${base}/${city}/${t.slug}`}
                  className="rounded-full border border-slate-200 px-2.5 py-1 text-xs text-slate-700 hover:border-primary hover:text-primary"
                >
                  {formatCityName(city)}
                </Link>
              ))}
            </div>
          </section>
        ))}

        {pageCount > 1 && (
          <nav className="mt-8 flex items-center justify-between border-t border-slate-200 pt-6 text-sm">
            {page > 1
              ? <Link to={page === 2 ? base : `${base}?page=${page - 1}`} className="font-semibold text-primary hover:underline">← Page précédente</Link>
              : <span />}
            <span className="text-slate-500">Page {page} / {pageCount}</span>
            {page < pageCount
              ? <Link to={`${base}?page=${page + 1}`} className="font-semibold text-primary hover:underline">Page suivante →</Link>
              : <span />}
          </nav>
        )}
      </div>

      <Footer />
    </div>
  )
}
